import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { NavLink as RouterLink, useLocation } from "react-router-dom";
// @mui
import { Box, Collapse, List, ListItemText } from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
//
import { StyledNavItem, StyledNavItemIcon } from "./styles";

// ----------------------------------------------------------------------

const activeItemSx = {
  "&.active, &.nav-active": {
    color: "text.primary",
    bgcolor: "action.selected",
    fontWeight: "fontWeightBold",
  },
};

NavSection.propTypes = {
  data: PropTypes.array,
};

export default function NavSection({ data = [], ...other }) {
  return (
    <Box {...other}>
      <List disablePadding sx={{ p: 1 }}>
        {data.map((item) => (
          <NavItem key={item.title} item={item} />
        ))}
      </List>
    </Box>
  );
}

// ----------------------------------------------------------------------

NavItem.propTypes = {
  item: PropTypes.object,
  depth: PropTypes.number,
};

function NavItem({ item, depth = 0 }) {
  const { pathname } = useLocation();
  const { title, path, matchPaths, icon, info, children } = item;
  const hasChildren = Array.isArray(children) && children.length > 0;
  const childActive =
    hasChildren && children.some((child) => isItemActive(pathname, child));
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (childActive) {
      setOpen(true);
    }
  }, [childActive]);

  if (hasChildren) {
    return (
      <>
        <StyledNavItem onClick={() => setOpen((value) => !value)}>
          <StyledNavItemIcon>{icon && icon}</StyledNavItemIcon>
          <ListItemText disableTypography primary={title} />
          {open ? (
            <ExpandLess sx={{ width: 20, height: 20 }} />
          ) : (
            <ExpandMore sx={{ width: 20, height: 20 }} />
          )}
        </StyledNavItem>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding>
            {children.map((child) => (
              <NavItem key={child.title} item={child} depth={depth + 1} />
            ))}
          </List>
        </Collapse>
      </>
    );
  }

  const active = isPathActive(pathname, path, matchPaths);

  return (
    <StyledNavItem
      component={RouterLink}
      to={path}
      className={active ? "nav-active" : undefined}
      sx={{
        ...(depth > 0 && { pl: 2 + depth * 2 }),
        ...activeItemSx,
      }}
    >
      <StyledNavItemIcon>{icon && icon}</StyledNavItemIcon>

      <ListItemText disableTypography primary={title} />

      {info && info}
    </StyledNavItem>
  );
}

function isItemActive(pathname, item) {
  if (isPathActive(pathname, item.path, item.matchPaths)) return true;
  return (
    Array.isArray(item.children) &&
    item.children.some((child) => isItemActive(pathname, child))
  );
}

function isPathActive(pathname, path, matchPaths = []) {
  return [path, ...matchPaths].filter(Boolean).some(
    (candidate) =>
      pathname === candidate || pathname.startsWith(`${candidate}/`)
  );
}
